/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call */
// Params and form controls from paramService have any types
import { Component, OnInit, DestroyRef, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import moment from 'moment';
import { MonEventTriggerModule } from '@monsido/angular-shared-components';
import { ActionTriggerService } from '@client/app/shared-modules/tracking/action-trigger/action-trigger.service';
import { AVAILABLE_PAGE_SIZE, DEFAULT_PAGE_SIZE, DEFAULT_DROPDOWN_SIZE } from '@client/app/shared-modules/api/constants/page-size.constant';
import { ParamService } from '@monsido/core/param/param.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UserRepoService, UserRepoRequestType } from '@client/app/feature-modules/admin/modules/admin-users/services/user-repo.service';
import { AdminLayoutService } from '@client/app/feature-modules/admin/services/admin-layout.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { TableModule, TablePageEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ConfirmationService, MenuItem, SortEvent } from 'primeng/api';
import { GlobalToastMessageService } from '@client/app/shared-modules/global-toast/services/global-toast-message.service';
import { MenuModule } from 'primeng/menu';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { StateService } from '@uirouter/core';
import { MyExportsDialogService } from '@client/app/shared-modules/my-exports/services/my-exports-dialog.service';
import { CreateExportDialogService } from '@client/app/shared-modules/create-export/services/create-export-dialog.service';
import { ComputedUser } from '@client/app/feature-modules/admin/modules/admin-users/types/user.type';
import { LabelSelectComponent } from '@client/app/feature-modules/shared/label/components/label-select/label-select.component';
import { LabelDataService } from '@client/app/feature-modules/shared/label/services/label-data.service';
import { LabelSelectModel } from '@client/app/feature-modules/shared/label/components/label-select/label-select.type';
import { Writable } from '@client/types/helpers/writable.type';
import { FilterObject, FilterOption, FilterResponse } from '@client/app/shared-modules/filter/types/filter.type';
import { LabelsRepo } from '@client/app/feature-modules/shared/label/services/labels.repo';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';
import { TableWrapperComponent } from '@client/app/shared-modules/table/components/table-wrapper.component';
import { TableCellTemplateDirective } from '@client/app/shared-modules/table/directives/table-cell-template.directive';

@Component({
    selector: 'mon-admin-users',
    imports: [
        CommonModule,
        TranslocoModule,
        TableModule,
        ButtonModule,
        MonEventTriggerModule,
        ConfirmDialog,
        MenuModule,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        DynamicDialogModule,
        LabelSelectComponent,
        PageLayoutComponent,
        TableWrapperComponent,
        TableCellTemplateDirective,
    ],
    providers: [
        ConfirmationService,
        DialogService,
        MyExportsDialogService,
        CreateExportDialogService,
        LabelDataService,
    ],
    templateUrl: './admin-users.component.html',
    styleUrls: ['./admin-users.component.scss'],
})
export class AdminUsersComponent implements OnInit {
    private readonly translocoService = inject(TranslocoService);

    readonly AVAILABLE_PAGE_SIZE = AVAILABLE_PAGE_SIZE;
    readonly TABLE_SCHEMA = TableWrapperComponent.createTableSchema<ComputedUser>({
        NAME: {
            label: this.translocoService.translate('admin.users.table.user'),
            id: 'first_name',
            type: 'property',
            sortable: true,
        },
        EMAIL: {
            label: this.translocoService.translate('admin.users.table.email'),
            id: 'email',
            type: 'property',
        },
        LABELS: {
            label: this.translocoService.translate('admin.users.table.labels'),
            id: 'labels',
            type: 'property',
        },
        ROLE: {

            label: this.translocoService.translate('admin.users.table.role'),
            id: 'role',
            type: 'property',
        },
        LAST_LOGIN: {
            label: this.translocoService.translate('admin.users.table.lastLogin'),
            id: 'last_login_at',
            type: 'property',
            sortable: true,
        },
        ACTIONS: {
            label: this.translocoService.translate('admin.users.table.actions'),
            id: 'actions',
            type: 'custom',
            actionColumn: true,
        },
    });

    @ViewChild(TableWrapperComponent) private readonly tableWrapper?: TableWrapperComponent<ComputedUser>;

    private menuTriggerElement: HTMLElement | null = null;

    private readonly DEFAULT_SORT_BY = 'first_name';
    private readonly DEFAULT_SORT_DIR = 'asc';

    users: ComputedUser[] = [];
    perPage = 0;
    total = 0;
    paginationTotal: number = 0;
    searchText = '';
    loading = true;
    loadingTotal = true;
    page: number = 1;
    pageSize: number = DEFAULT_PAGE_SIZE;
    sortBy: string = this.DEFAULT_SORT_BY;
    sortDir: string = this.DEFAULT_SORT_DIR;
    private readonly searchSubject = new Subject<string>();
    private readonly destroy$ = new Subject<void>();
    filters: FilterObject = {};
    filterResponse: FilterResponse = {};
    appliedFilters: FilterResponse = {};

    constructor (
        private readonly actionTriggerService: ActionTriggerService,
        private readonly paramService: ParamService,
        private readonly confirmationService: ConfirmationService,
        private readonly globalToastMessageService: GlobalToastMessageService,
        private readonly adminLayoutService: AdminLayoutService,
        private readonly userRepoService: UserRepoService,
        private readonly destroyRef: DestroyRef,
        private readonly labelsRepoService: LabelsRepo,
        private readonly stateService: StateService,
        public readonly labelDataService: LabelDataService,
    ) {
        this.searchSubject
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this.destroy$),
            ).subscribe((searchText) => {
                this.searchUsers(searchText);
            });
    }

    ngOnInit (): void {
        this.setBreadcrumbs();

        const params = this.paramService.getParams();
        this.page = Number(params.page) || 1;
        this.pageSize = Number(params.page_size) || DEFAULT_PAGE_SIZE;
        this.sortBy = params.sort_by || this.DEFAULT_SORT_BY;
        this.sortDir = params.sort_dir || this.DEFAULT_SORT_DIR;
        this.searchText = params.search || '';

        this.setFilters();
        this.getTotalUsers();
        this.labelDataService.fetch({ page: 1, pageSize: 20 });

        const deregisterHook = this.paramService.subscribeParamsChangeOnCurrentState((updatedParams) => {
            this.page = updatedParams.page ?? 1;
            this.pageSize = updatedParams.page_size ?? 10;
            this.sortBy = updatedParams.sort_by ?? this.DEFAULT_SORT_BY;
            this.sortDir = updatedParams.sort_dir ?? this.DEFAULT_SORT_DIR;
            this.searchText = updatedParams.search ?? '';
            this.getUsers();
        });

        this.destroyRef.onDestroy(() => {
            deregisterHook();
            this.destroy$.next();
            this.destroy$.complete();
        });
    }

    setBreadcrumbs (): void {
        this.adminLayoutService.setBreadcrumbs({
            uiStates: [
                {
                    label: this.translocoService.translate('admin.users.table.breadcrumbLabel'),
                    state: 'base.customer.admin.users',
                },
            ],
        });
    }

    async setFilters (): Promise<void> {
        const labels = await this.labelsRepoService.getLabels({ page_size: DEFAULT_DROPDOWN_SIZE });
        this.filters = {
            role: {
                type: 'select',
                options: [
                    { name: this.translocoService.translate('admin.users.role.filterLabel.admin'), value: true },
                    { name: this.translocoService.translate('admin.users.role.filterLabel.regular'), value: false },
                ],
                placeholder: this.translocoService.translate('admin.users.role.filterLabelPlaceholder'),
            },
            label: {
                type: 'multi-select',
                options: labels.map(label => ({ name: label.name, value: label.id })),
                placeholder: this.translocoService.translate('admin.users.label.filterLabelPlaceholder'),
                total: labels.total || 0,
                validateFn: async (value: FilterResponse) => {
                    return await this.verifyLabelsFilter(value.label);
                },
                searchFn: async (searchText: string) => {
                    return this.searchLabelFilter(searchText);
                },
            },
        };
    }

    loadMorefilterOptions (event: { filterKey: string, page: number }) {
        this.fetchLabels(event.filterKey, event.page);
    }

    async fetchLabels (filterKey: string, page: number) {
        const labels = await this.labelsRepoService.getLabels({ page: page, page_size: DEFAULT_DROPDOWN_SIZE });
        const newOptions = labels.map(label => ({ name: label.name, value: label.id }));
        if (!Array.isArray(this.filters[filterKey].options)) {
            this.filters[filterKey].options = [];
        }
        const existingIds = new Set(this.filters[filterKey].options.map(opt => opt.value));
        const currentOptions = structuredClone(this.filters[filterKey].options);
        for (const opt of newOptions) {
            if (!existingIds.has(opt.value)) {
                currentOptions.push(opt);
            }
        }
        this.filters[filterKey].options = currentOptions;
    }

    async verifyLabelsFilter (value: FilterOption | FilterOption[] | null): Promise<FilterResponse> {
        if (!value) {
            return { label: [] };
        }
        const values = Array.isArray(value) ? value.map(v => v.value) : [value.value];
        const verifiedLabels = await this.labelsRepoService.getLabels({ 'ids[]': values, page_size: 100 }).then(labels => {
            return labels.map(label => ({ name: label.name, value: label.id }));
        });
        return {
            label: verifiedLabels,
        };
    }

    async searchLabelFilter (searchText: string): Promise<FilterOption[]> {
        if (!searchText) {
            return [];
        }
        const searchedLabels = await this.labelsRepoService.getLabels({ search: searchText, page_size: DEFAULT_DROPDOWN_SIZE }).then(labels => {
            return labels.map(label => ({ name: label.name, value: label.id }));
        });
        return searchedLabels;
    }

    onFiltersApplied ($event: FilterResponse): void {
        this.filterResponse = $event;
        this.getUsers();
    }

    async getUsers (): Promise<void> {
        this.loading = true;
        try {
            const params: UserRepoRequestType = {
                page: Number(this.page),
                page_size: Number(this.pageSize),
                search: this.searchText,
                sort_by: this.sortBy,
                sort_dir: this.sortDir,
            };
            if (this.filterResponse.role !== undefined && this.filterResponse.role !== null) {
                if (!Array.isArray(this.filterResponse.role)) {
                    params.admin = this.filterResponse.role.value;
                }
            }

            if (this.filterResponse.label !== undefined && this.filterResponse.label !== null) {
                if (Array.isArray(this.filterResponse.label)) {
                    params['labels[]'] = this.filterResponse.label.map(label => label.value);
                } else {
                    params['labels[]'] = this.filterResponse.label.value;
                }
            }
            const usersResponse = await this.userRepoService.getAll(params);

            if (!usersResponse) {
                return;
            }

            this.perPage = usersResponse.perPage || 0;
            this.paginationTotal = usersResponse.total || 0;
            this.users = usersResponse
                .filter(user => user.id && user.email)
                .map(user => ({
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    id: user.id!,
                    first_name: user.first_name ?? '',
                    last_name: user.last_name ?? '',
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    email: user.email!,
                    customer_admin: user.customer_admin ?? '',
                    last_login_at: user.last_login_at
                        ? moment(user.last_login_at).format('MMM DD, YYYY')
                        : this.translocoService.translate('admin.users.last_login.never'),
                    role: user.customer_admin ? 'Admin' : 'Regular',
                    labels: user.labels || [],
                    actionItems: [
                        {
                            label: this.translocoService.translate('admin.users.actions.editButtonLabel'),
                            icon: 'pi pi-pencil',
                            command: () => this.editUser(user),
                        },
                        {
                            label: this.translocoService.translate('admin.users.actions.deleteButtonLabel'),
                            icon: 'pi pi-trash',
                            command: () => this.deleteUser(user),
                        },
                    ] as MenuItem[],
                }));
            this.appliedFilters = { ...this.filterResponse };
        } finally {
            this.loading = false;

        }
    }

    async getTotalUsers (): Promise<void> {
        this.loadingTotal = true;
        try {
            const usersResponse = await this.userRepoService.getAll({
                page: 1,
                page_size: 1,
            });

            if (!usersResponse) {
                return;
            }

            this.total = usersResponse.total || 0;
        } finally {
            this.loadingTotal = false;
        }
    }

    sortUsers (event: SortEvent) {
        const { field, order } = event;
        const sortDir = order === 1 ? 'asc' : 'desc';

        if (sortDir === this.sortDir && field === this.sortBy) {
            return;
        }

        this.sortBy = field || this.DEFAULT_SORT_BY;
        this.sortDir = sortDir;

        this.paramService.setParams({
            page: 1,
            sort_by: this.sortBy,
            sort_dir: this.sortDir,
        });
    }

    onPageChange (pageChangeEvent: TablePageEvent) {
        const { first, rows } = pageChangeEvent;

        // Check if page size has changed
        const pageSizeChanged = rows !== this.perPage;

        // If page size changed, reset to page 1; otherwise calculate current page
        const pageNumber = pageSizeChanged ? 1 : Math.floor((first || 0) / (rows || 1)) + 1;

        this.paramService.updateParams({
            page: pageNumber,
            page_size: rows,
        });
    }

    onSearchInput (event: Event) {
        const searchValue = (event.target as HTMLInputElement).value;
        this.searchSubject.next(searchValue);
    }

    private searchUsers (searchText: string) {
        this.searchText = searchText;
        this.paramService.updateParams({
            page: 1,
            search: this.searchText,
        });
    }

    addUser (): void {
        this.actionTriggerService.track({ action: 'admin-users-add-user-btn' });

        this.stateService.go('base.customer.admin.add-user');
    }

    editUser (user: ComputedUser): void {
        this.actionTriggerService.track({ action: 'admin-users-edit-user-btn' });

        this.stateService.go('base.customer.admin.edit-user', {
            id: user.id,
        });
    }

    public onMenuHide (): void {
        const el = document.activeElement;
        this.menuTriggerElement = (el instanceof HTMLElement) ? el : null;
    }

    deleteUser (selectedUser: ComputedUser): void {
        this.tableWrapper?.trackAction(this.users, selectedUser, 'delete', () => true);
        this.actionTriggerService.track({ action: 'admin-users-delete-user-btn' });
        this.confirmationService.confirm({
            message: this.translocoService.translate('admin.users.deleteDialog.message'),
            header: this.translocoService.translate('admin.users.deleteDialog.header'),
            closable: true,
            closeOnEscape: true,
            dismissableMask: true,
            rejectButtonProps: {
                label: this.translocoService.translate('admin.users.deleteUserDialog.rejectButtonLabel'),
                severity: 'secondary',
                variant: 'text',
            },
            acceptButtonProps: {
                label: this.translocoService.translate('admin.users.deleteUserDialog.acceptButtonLabel'),
                severity: 'danger',
            },
            reject: () => {
                this.tableWrapper?.focusService?.reset();
                this.menuTriggerElement?.focus();
                this.menuTriggerElement = null;
            },
            accept: async () => {
                this.loading = true;
                await this.userRepoService.delete({
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    id: selectedUser.id!,
                });
                this.globalToastMessageService.info(
                    this.translocoService.translate('admin.users.deleteSuccessToast.message{{userName}}', {
                        userName: selectedUser.first_name + (selectedUser.last_name ? ' ' + selectedUser.last_name : ''),
                    }),
                );
                await this.reload();
            },
        });
    }

    async reload (): Promise<void> {
        this.getTotalUsers();
        return this.getUsers();
    }

    updateUserLabels (user: ComputedUser, labels: LabelSelectModel[]) {
        if (!user.id) {
            return;
        }
        const labelsPayload: number[] = [];
        for (const label of labels) {
            if (label.id) {
                labelsPayload.push(label.id);
            }
        }
        this.userRepoService.update({
            id: user.id,
            labels: labelsPayload,
        })
            .then(() => {
                this.globalToastMessageService.success(
                    this.translocoService.translate('admin.users.updateLabelSuccessToast.summary{{userEmail}}', {
                        userEmail: user.email,
                    }),
                );
            })
            .catch(() => {
                this.globalToastMessageService.error(
                    this.translocoService.translate('admin.users.updateLabelErrorToast.summary{{userEmail}}', {
                        userEmail: user.email,
                    }),
                );
            });
    }

    async addNewLabelToUser (user: ComputedUser, params) {
        try {
            const label = await this.labelDataService.createAndAppendLabel(params);
            const copy = (user.labels || []).slice();
            copy.push(label);
            (user as Writable<ComputedUser>).labels = copy;
            if (!user.id) {
                return;
            }
            const labelIds: number[] = [];
            for (const l of user?.labels || []) {
                if (l.id) {
                    labelIds.push(l.id);
                }
            }
            await this.userRepoService.update({
                id: user.id,
                labels: labelIds,
            });

            this.globalToastMessageService.success(
                this.translocoService.translate('admin.users.addLabelToUserSuccess.details{{userEmail}}{{labelName}}', {
                    userEmail: user.email,
                    labelName: label.name,
                }),
            );
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to update user labels:', e);
            this.globalToastMessageService.error(
                this.translocoService.translate('admin.users.updateLabelErrorToast.summary{{userEmail}}', {
                    userEmail: user.email,
                }),
            );
        }
    }
}
